import {
    BarcodeScanner,
    BarcodeFormat,
    LensFacing,
} from '@capacitor-mlkit/barcode-scanning';
import type { Barcode } from '@capacitor-mlkit/barcode-scanning';

export { BarcodeFormat };
export type { Barcode };

// ── Format constants ──────────────────────────────────────────────────

export const BARCODE_1D_FORMATS: BarcodeFormat[] = [
    BarcodeFormat.Itf,
    BarcodeFormat.Code39,
    BarcodeFormat.Code93,
    BarcodeFormat.Code128,
    BarcodeFormat.Codabar,
];

export const BARCODE_1D_FORMATS_WITH_UPC: BarcodeFormat[] = [
    ...BARCODE_1D_FORMATS,
    BarcodeFormat.UpcA,
    BarcodeFormat.UpcE,
];

export const BARCODE_QR_FORMATS: BarcodeFormat[] = [
    BarcodeFormat.QrCode,
];

export const BARCODE_QR_AND_DATAMATRIX: BarcodeFormat[] = [
    BarcodeFormat.QrCode,
    BarcodeFormat.DataMatrix,
];

export const BARCODE_1D_AND_QR_FORMATS: BarcodeFormat[] = [
    ...BARCODE_1D_FORMATS,
    BarcodeFormat.QrCode,
];

// ── Types ─────────────────────────────────────────────────────────────

export type ContinuousScanHandle = {
    stop: () => Promise<void>;
};

type ContinuousScanOptions = {
    enableTorch?: boolean;
    torchDelay?: number;
};

// ── WebView transparency ──────────────────────────────────────────────
// MLKit's startScan() renders the camera preview behind the WebView.
// We add a CSS class that makes backgrounds transparent so the camera
// is visible, while keeping UI elements (buttons, headers) on top.

export const makeWebViewTransparent = (): void => {
    document.documentElement.classList.add('barcode-scanning-active');
};

export const restoreWebView = (): void => {
    document.documentElement.classList.remove('barcode-scanning-active');
    document.documentElement.classList.remove('barcode-scanning-fullscreen');
};

// For modals: hide entire WebView so camera is visible fullscreen
export const makeWebViewHidden = (): void => {
    document.documentElement.classList.add('barcode-scanning-fullscreen');
};

// ── Permission ────────────────────────────────────────────────────────

export const checkCameraPermission = async (): Promise<boolean> => {
    const { camera } = await BarcodeScanner.checkPermissions();
    if (camera === 'granted') return true;
    if (camera === 'denied') return false;

    const result = await BarcodeScanner.requestPermissions();
    return result.camera === 'granted';
};

// ── Continuous scan ───────────────────────────────────────────────────

export const startContinuousScan = async (
    formats: BarcodeFormat[],
    onBarcode: (rawValue: string) => void,
    options?: ContinuousScanOptions,
): Promise<ContinuousScanHandle> => {
    let torchTimeout: NodeJS.Timeout | null = null;
    let torchEnabled = false;

    const listener = await BarcodeScanner.addListener(
        'barcodesScanned',
        (event) => {
            const barcodes: Barcode[] = (event as any).barcodes ?? [];
            for (const barcode of barcodes) {
                if (barcode?.rawValue) {
                    onBarcode(barcode.rawValue);
                }
            }
        },
    );

    await BarcodeScanner.startScan({ formats, lensFacing: LensFacing.Back });

    if (options?.enableTorch) {
        torchTimeout = setTimeout(async () => {
            try {
                await BarcodeScanner.enableTorch();
                torchEnabled = true;
            } catch (_) {
                // torch not available on device
            }
        }, options.torchDelay ?? 1000);
    }

    return {
        stop: async () => {
            if (torchTimeout) {
                clearTimeout(torchTimeout);
                torchTimeout = null;
            }
            await listener.remove();
            try {
                await BarcodeScanner.stopScan();
            } catch (_) {
                // may already be stopped
            }
            if (torchEnabled) {
                try {
                    await BarcodeScanner.disableTorch();
                } catch (_) {}
            }
        },
    };
};

// ── Batch continuous scan ─────────────────────────────────────────────
// Uses 'barcodesScanned' (plural) event — receives ALL barcodes detected
// in a single camera frame at once, as an array of rawValues.

export const startBatchContinuousScan = async (
    formats: BarcodeFormat[],
    onBarcodes: (barcodes: Barcode[]) => void,
    options?: ContinuousScanOptions,
): Promise<ContinuousScanHandle> => {
    let torchTimeout: NodeJS.Timeout | null = null;
    let torchEnabled = false;

    const listener = await BarcodeScanner.addListener(
        'barcodesScanned',
        (event) => {
            const barcodes: Barcode[] = (event as any).barcodes ?? [];
            if (barcodes.length > 0) {
                onBarcodes(barcodes);
            }
        },
    );

    await BarcodeScanner.startScan({ formats, lensFacing: LensFacing.Back });

    if (options?.enableTorch) {
        torchTimeout = setTimeout(async () => {
            try {
                await BarcodeScanner.enableTorch();
                torchEnabled = true;
            } catch (_) {
                // torch not available on device
            }
        }, options.torchDelay ?? 1000);
    }

    return {
        stop: async () => {
            if (torchTimeout) {
                clearTimeout(torchTimeout);
                torchTimeout = null;
            }
            await listener.remove();
            try {
                await BarcodeScanner.stopScan();
            } catch (_) {
                // may already be stopped
            }
            if (torchEnabled) {
                try {
                    await BarcodeScanner.disableTorch();
                } catch (_) {}
            }
        },
    };
};

// ── Single scan (native UI overlay) ──────────────────────────────────
// Opens MLKit's built-in scanner UI. No custom overlay possible.

export const singleScan = async (
    formats: BarcodeFormat[],
): Promise<string | null> => {
    const { barcodes } = await BarcodeScanner.scan({ formats });
    return barcodes[0]?.rawValue ?? null;
};

// ── Torch toggle ─────────────────────────────────────────────────────

export const toggleTorch = async (): Promise<void> => {
    try {
        const { enabled } = await BarcodeScanner.isTorchEnabled();
        if (enabled) {
            await BarcodeScanner.disableTorch();
        } else {
            await BarcodeScanner.enableTorch();
        }
    } catch (_) {
        // torch not available on device
    }
};

// ── Settings ──────────────────────────────────────────────────────────

export const openSettings = async (): Promise<void> => {
    await BarcodeScanner.openSettings();
};

// ── Stop (safe cleanup) ──────────────────────────────────────────────

export const stopAllScanning = async (): Promise<void> => {
    try {
        await BarcodeScanner.stopScan();
    } catch (_) {
        // may not be scanning
    }
};
